import {
  createApiRef,
  type DiscoveryApi,
  type FetchApi,
} from '@backstage/core-plugin-api';
import type {
  TestMetricsResponse,
  AvailabilityResponse,
  TestMetricsOptions,
  AvailabilityOptions,
  TestMetricRow,
  AvailabilityMetricRow,
  AlarmRow,
  AlarmsResponse,
  AlarmsOptions,
} from './types';

export interface MetricsApi {
  getTestMetrics(options: TestMetricsOptions): Promise<TestMetricsResponse>;
  getAvailabilityMetrics(
    options: AvailabilityOptions,
  ): Promise<AvailabilityResponse>;
  getAlarms(options?: AlarmsOptions): Promise<AlarmsResponse>;
}

export const metricsApiRef = createApiRef<MetricsApi>({
  id: 'plugin.metrics.service',
});

function isTestMetricRow(value: unknown): value is TestMetricRow {
  if (!value || typeof value !== 'object') return false;
  const v = value as Record<string, unknown>;
  return (
    typeof v.time === 'string' &&
    typeof v.application === 'string' &&
    typeof v.repository === 'string' &&
    typeof v.coveragePercentage === 'number' &&
    typeof v.testCount === 'number'
  );
}

function isAvailabilityMetricRow(
  value: unknown,
): value is AvailabilityMetricRow {
  if (!value || typeof value !== 'object') return false;
  const v = value as Record<string, unknown>;
  return (
    typeof v.time === 'string' &&
    typeof v.canaryName === 'string' &&
    typeof v.successPercent === 'number'
  );
}

function parseTestMetricsResponse(payload: unknown): TestMetricsResponse {
  if (!payload || typeof payload !== 'object') {
    throw new Error('Invalid test metrics response: expected an object');
  }
  const metrics = (payload as { metrics?: unknown }).metrics;
  if (!Array.isArray(metrics) || !metrics.every(isTestMetricRow)) {
    throw new Error('Invalid test metrics response: malformed metrics array');
  }
  return { metrics };
}

function parseAvailabilityResponse(payload: unknown): AvailabilityResponse {
  if (!payload || typeof payload !== 'object') {
    throw new Error('Invalid availability response: expected an object');
  }
  const metrics = (payload as { metrics?: unknown }).metrics;
  if (!Array.isArray(metrics) || !metrics.every(isAvailabilityMetricRow)) {
    throw new Error('Invalid availability response: malformed metrics array');
  }
  return { metrics };
}

function isAlarmRow(value: unknown): value is AlarmRow {
  if (!value || typeof value !== 'object') return false;
  const v = value as Record<string, unknown>;
  const strings = [
    'name',
    'arn',
    'accountId',
    'accountName',
    'state',
    'stateReason',
    'description',
    'stateSince',
    'applicationName',
    'applicationRepo',
    'severity',
    'runbookUrl',
    'consoleUrl',
  ] as const;
  return (
    strings.every(key => typeof v[key] === 'string') &&
    (v.firesLast7Days === null || typeof v.firesLast7Days === 'number')
  );
}

function parseAlarmsResponse(payload: unknown): AlarmsResponse {
  if (!payload || typeof payload !== 'object') {
    throw new Error('Invalid alarms response: expected an object');
  }
  const { alarms, failedAccounts, updatedAt } = payload as Record<
    string,
    unknown
  >;
  if (!Array.isArray(alarms) || !alarms.every(isAlarmRow)) {
    throw new Error('Invalid alarms response: malformed alarms array');
  }
  return {
    alarms,
    failedAccounts: Array.isArray(failedAccounts)
      ? failedAccounts.filter((a): a is string => typeof a === 'string')
      : [],
    updatedAt: typeof updatedAt === 'string' ? updatedAt : null,
  };
}

export class MetricsClient implements MetricsApi {
  private readonly discoveryApi: DiscoveryApi;
  private readonly fetchApi: FetchApi;

  constructor(options: { discoveryApi: DiscoveryApi; fetchApi: FetchApi }) {
    this.discoveryApi = options.discoveryApi;
    this.fetchApi = options.fetchApi;
  }

  async getTestMetrics(
    options: TestMetricsOptions,
  ): Promise<TestMetricsResponse> {
    const baseUrl = await this.discoveryApi.getBaseUrl('metrics');
    const params = new URLSearchParams({ repository: options.repository });
    if (options.application) params.set('application', options.application);
    if (options.branch) params.set('branch', options.branch);
    if (options.days) params.set('days', String(options.days));

    const response = await this.fetchApi.fetch(
      `${baseUrl}/tests?${params.toString()}`,
    );
    if (!response.ok) {
      throw new Error(
        `Failed to fetch test metrics: ${response.status} ${response.statusText}`,
      );
    }
    return parseTestMetricsResponse(await response.json());
  }

  async getAvailabilityMetrics(
    options: AvailabilityOptions,
  ): Promise<AvailabilityResponse> {
    const baseUrl = await this.discoveryApi.getBaseUrl('metrics');
    const params = new URLSearchParams({ repository: options.repository });
    if (options.days) params.set('days', String(options.days));

    const response = await this.fetchApi.fetch(
      `${baseUrl}/availability?${params.toString()}`,
    );
    if (!response.ok) {
      throw new Error(
        `Failed to fetch availability metrics: ${response.status} ${response.statusText}`,
      );
    }
    return parseAvailabilityResponse(await response.json());
  }

  async getAlarms(options: AlarmsOptions = {}): Promise<AlarmsResponse> {
    const baseUrl = await this.discoveryApi.getBaseUrl('metrics');
    const params = new URLSearchParams();
    if (options.repository) params.set('repository', options.repository);
    if (options.application) params.set('application', options.application);

    const response = await this.fetchApi.fetch(
      `${baseUrl}/alarms?${params.toString()}`,
    );
    if (!response.ok) {
      throw new Error(
        `Failed to fetch alarms: ${response.status} ${response.statusText}`,
      );
    }
    return parseAlarmsResponse(await response.json());
  }
}
